import getVariablePlainText from '@/lib/get-variable-plain-text';
import { ImageAsset, Variable } from '@/types/views/generic';

type VariablesByKey = Record<string, Variable['text']>;

const resolveText = (
  variables: VariablesByKey,
  slug: string,
  fallback: string,
): string => getVariablePlainText(variables[slug]) || fallback;

export const ONBOARDING_NEWSLETTER_VARIABLE_SLUGS = {
  titlePrefix: 'onboarding-newsletter-title-prefix',
  titleBrand: 'onboarding-newsletter-title-brand',
  description: 'onboarding-newsletter-description',
  image: 'onboarding-newsletter-image',
  emailLabel: 'onboarding-newsletter-email-label',
  firstNameLabel: 'onboarding-newsletter-first-name-label',
  lastNameLabel: 'onboarding-newsletter-last-name-label',
  consentLabel: 'onboarding-newsletter-consent-label',
  consentDescription: 'onboarding-newsletter-consent-description',
  privacyLinkLabel: 'onboarding-newsletter-privacy-link-label',
  privacyLinkHref: 'onboarding-newsletter-privacy-link-href',
  submitButton: 'onboarding-newsletter-submit-button',
  dismissButton: 'onboarding-newsletter-dismiss-button',
  successTitle: 'onboarding-newsletter-success-title',
  successMessage: 'onboarding-newsletter-success-message',
  confirmedMessage: 'onboarding-newsletter-confirmed-message',
  errorMessage: 'onboarding-newsletter-error-message',
  invalidEmailMessage: 'onboarding-newsletter-invalid-email-message',
  rateLimitMessage: 'onboarding-newsletter-rate-limit-message',
  sessionExpiredMessage: 'onboarding-newsletter-session-expired-message',
} as const;

const DEFAULT_ONBOARDING_NEWSLETTER_TEXTS = {
  titlePrefix: 'Willkommen bei',
  titleBrand: 'Energy',
  description:
    'Schön, dass du dabei bist. Abonniere unseren Newsletter und verpasse keine News, Aktionen und Highlights.',
  emailLabel: 'E-Mail Adresse',
  firstNameLabel: 'Vorname',
  lastNameLabel: 'Nachname',
  consentLabel: 'Newsletter abonnieren',
  consentDescription:
    'Ich möchte den Energy Newsletter erhalten und bin mit der {privacyLink} einverstanden.',
  privacyLinkLabel: 'Datenschutzerklärung',
  privacyLinkHref: '/rechtliches/datenschutz',
  submitButton: 'Newsletter abonnieren',
  dismissButton: 'Jetzt nicht',
  successTitle: 'Fast geschafft',
  successMessage:
    'Bitte bestätige deine E-Mail-Adresse, um den Newsletter zu aktivieren.',
  confirmedMessage: 'Danke! Du bist für den Newsletter angemeldet.',
  errorMessage:
    'Etwas ist schiefgelaufen. Bitte versuche es später noch einmal.',
  invalidEmailMessage: 'Bitte gib eine gültige E-Mail-Adresse ein.',
  rateLimitMessage:
    'Zu viele Anfragen. Bitte warte einige Minuten und versuche es erneut.',
  sessionExpiredMessage:
    'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
} as const;

export type OnboardingNewsletterTexts = {
  [K in keyof typeof DEFAULT_ONBOARDING_NEWSLETTER_TEXTS]: string;
};

export const getOnboardingNewsletterTexts = (
  variables: VariablesByKey,
): OnboardingNewsletterTexts => ({
  titlePrefix: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.titlePrefix,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.titlePrefix,
  ),
  titleBrand: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.titleBrand,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.titleBrand,
  ),
  description: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.description,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.description,
  ),
  emailLabel: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.emailLabel,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.emailLabel,
  ),
  firstNameLabel: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.firstNameLabel,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.firstNameLabel,
  ),
  lastNameLabel: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.lastNameLabel,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.lastNameLabel,
  ),
  consentLabel: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.consentLabel,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.consentLabel,
  ),
  consentDescription: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.consentDescription,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.consentDescription,
  ),
  privacyLinkLabel: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.privacyLinkLabel,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.privacyLinkLabel,
  ),
  privacyLinkHref: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.privacyLinkHref,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.privacyLinkHref,
  ),
  submitButton: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.submitButton,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.submitButton,
  ),
  dismissButton: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.dismissButton,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.dismissButton,
  ),
  successTitle: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.successTitle,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.successTitle,
  ),
  successMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.successMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.successMessage,
  ),
  confirmedMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.confirmedMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.confirmedMessage,
  ),
  errorMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.errorMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.errorMessage,
  ),
  invalidEmailMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.invalidEmailMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.invalidEmailMessage,
  ),
  rateLimitMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.rateLimitMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.rateLimitMessage,
  ),
  sessionExpiredMessage: resolveText(
    variables,
    ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.sessionExpiredMessage,
    DEFAULT_ONBOARDING_NEWSLETTER_TEXTS.sessionExpiredMessage,
  ),
});

export const getOnboardingNewsletterImage = (
  variables: Variable[],
): ImageAsset | null => {
  const entry = variables.find(
    (variable) =>
      variable.slug === ONBOARDING_NEWSLETTER_VARIABLE_SLUGS.image &&
      variable.image,
  );

  return entry?.image ?? null;
};
